<template>
  <v-expansion-panels
    style="margin-bottom: 10px"
    :value="associatesPanel"
    @input="$emit('update:associatesPanel', $event)"
  >
    <v-expansion-panel>
      <v-expansion-panel-header disable-icon-rotate>
        Dodaj podatke o kupcu<span v-if="eRacunConditions.providerActive && paymentTypeValue === 'T'" class="grey--text font-weight-regular"> / eRačun</span>
        <template v-slot:actions>
          <v-icon> mdi-account-multiple-outline </v-icon>
        </template>
      </v-expansion-panel-header>
      <v-expansion-panel-content>
        <!-- Customer basic info -->
        <v-row dense>
          <v-col cols="4" sm="4" md="4">
            <v-combobox
              ref="nameCombobox"
              label="Kupac"
              outlined
              dense
              :items="associates"
              v-model="nameModel"
              :search-input.sync="nameSearchInput"
              @update:search-input="$emit('update:nameSearchInput', $event)"
              item-text="name"
              :disabled="!cashRegister"
              :loading="loadingName"
              clearable
              placeholder="POČNITE TIPKATI ZA PRETRAGU"
              return-object
            >
              <template v-slot:item="{ item, on, attrs }">
                <v-list-item v-on="on" v-bind="attrs">
                  <v-list-item-content>
                    <v-list-item-title>{{ item.name }}</v-list-item-title>
                    <v-list-item-subtitle v-if="item.oibs && item.oibs[0]">{{ item.oibs[0] }}</v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
              </template>
            </v-combobox>
          </v-col>
          <v-col cols="4" sm="4" md="4">
            <v-text-field
              :value="associateAddress"
              @input="$emit('update:associateAddress', $event)"
              label="Adresa"
              outlined
              dense
              persistent-hint
              :disabled="!cashRegister"
            ></v-text-field>
          </v-col>
          <v-col cols="4" sm="4" md="4">
            <v-combobox
              ref="oibCombobox"
              label="OIB"
              outlined
              dense
              :items="associatesWithOib"
              v-model="oibModel"
              :search-input.sync="oibSearchInput"
              @update:search-input="$emit('update:oibSearchInput', $event)"
              :item-text="item => item.oib || (item.oibs && item.oibs[0]) || ''"
              :disabled="!cashRegister"
              :loading="loadingOib"
              clearable
              placeholder="POČNITE TIPKATI ZA PRETRAGU"
              return-object
            >
              <template v-slot:item="{ item, on, attrs }">
                <v-list-item v-on="on" v-bind="attrs">
                  <v-list-item-content>
                    <v-list-item-title>{{ item.oib || (item.oibs && item.oibs[0]) || '' }}</v-list-item-title>
                    <v-list-item-subtitle>{{ item.name }}</v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
              </template>
            </v-combobox>
          </v-col>
        </v-row>

        <!-- Address details -->
        <v-row dense style="margin-top: -1.3rem">
          <v-col cols="4" sm="4" md="4">
            <v-text-field
              :value="associateZip"
              @input="$emit('update:associateZip', $event)"
              label="Poštanski broj"
              outlined
              dense
              persistent-hint
              :disabled="!cashRegister"
            ></v-text-field>
          </v-col>
          <v-col cols="4" sm="4" md="4">
            <v-text-field
              :value="associateCity"
              @input="$emit('update:associateCity', $event)"
              label="Grad"
              outlined
              dense
              persistent-hint
              :disabled="!cashRegister"
            ></v-text-field>
          </v-col>
          <v-col cols="4" sm="4" md="4">
            <v-select
              :items="countries"
              :label="countryLabel"
              :value="associateCountry"
              @input="$emit('update:associateCountry', $event)"
              outlined
              dense
              item-text="country"
              item-value="country"
              class="uppercased-input"
              :disabled="!cashRegister"
            ></v-select>
          </v-col>
        </v-row>

        <!-- Cost center and save associate -->
        <v-row
          dense
          style="margin-top: -1rem"
          class="pb-4"
          v-if="costCenters && costCenters.length > 0"
        >
          <v-col cols="6">
            <v-select
              :items="costCenters"
              label="Odaberite mjesto troška"
              :value="selectedCostCenter"
              @input="$emit('update:selectedCostCenter', $event)"
              outlined
              dense
              item-text="name"
              class="uppercased-input"
              hide-details
              return-object
            >
              <template v-slot:item="{ item }">
                <span>
                  {{ item.name }}
                  {{ item.description ? " - " + item.description : "" }}
                  {{ item.address && item.address.street ? ", " + item.address.street : "" }}
                  {{ item.address && item.address.city ? item.address.city : "" }}
                </span>
              </template>
            </v-select>
          </v-col>
          <v-col cols="2" class="pr-0">
            <v-btn
              @click="$emit('open-cost-center-dialog')"
              class="btn successButton"
            >
              <v-icon>mdi-plus</v-icon>
            </v-btn>
          </v-col>
          <v-col cols="4" v-if="saveAssociateEnabled" class="d-flex align-center justify-end">
            <span
              :style="{
                color: saveAssociateEnabled ? 'black' : '#d9d9d9',
              }"
              style="font-size: 0.875rem; white-space: nowrap;"
            >
              {{ saveAssociateLabel }}
            </span>
            <v-checkbox
              class="ml-2 mt-0 pt-0"
              :disabled="!saveAssociateEnabled"
              :input-value="doSaveAccount"
              @change="handleCheckboxChange"
              hide-details
            ></v-checkbox>
          </v-col>
        </v-row>

        <!-- VIES check for EU customers (non-Croatia) -->
        <v-row
          v-if="
            inEu &&
            associateCountry !== 'Hrvatska' &&
            associateCountry !== undefined &&
            isTransactionalReceipt
          "
          style="margin-top: -3rem"
        >
          <v-spacer></v-spacer>
          <v-col cols="4">
            <v-radio-group
              v-model="viesModel"
              :rules="[(v) => !!v || 'Molimo odaberite']"
              required
            >
              <v-radio
                v-for="vies in vieses"
                :key="vies.value"
                :value="vies.value"
                :label="vies.name"
              ></v-radio>
            </v-radio-group>
            <v-btn
              href="https://ec.europa.eu/taxation_customs/vies/?locale=hr"
              target="_blank"
            >
              Provjera VIES baze
            </v-btn>
          </v-col>
        </v-row>

        <!-- Customer type for non-EU customers (non-Croatia) -->
        <v-row
          v-if="
            !inEu &&
            associateCountry !== 'Hrvatska' &&
            associateCountry !== undefined &&
            isTransactionalReceipt
          "
          style="margin-top: -3rem"
        >
          <v-spacer></v-spacer>
          <v-col cols="4">
            <v-radio-group
              :value="selectedCustomerType"
              @input="$emit('update:selectedCustomerType', $event)"
              :rules="[(v) => !!v || 'Molimo odaberite']"
              required
              class="small-radio"
            >
              <v-radio
                v-for="type in customerTypes"
                :key="type.value"
                :value="type.value"
                :label="type.name"
              ></v-radio>
            </v-radio-group>
          </v-col>
        </v-row>

        <!-- eRacun section for transactional receipts -->
        <v-row
          v-if="paymentTypeValue === 'T'"
          dense
          class="mt-1 pb-1"
        >
          <v-col cols="12">
            <v-divider class="mb-3"></v-divider>
            <v-card
              outlined
              rounded="lg"
              :color="eracun ? 'blue lighten-5' : undefined"
              :class="['transition-card', { 'blue-border': eracun, 'disabled-card': !eRacunEnabled }]"
            >
              <v-card-text class="py-2 px-3">
                <div class="d-flex align-center">
                  <v-icon
                    :color="eracun ? 'primary' : (eRacunEnabled ? 'grey darken-1' : 'grey lighten-1')"
                    class="mr-3"
                    size="22"
                  >mdi-file-sign</v-icon>
                  <div class="flex-grow-1">
                    <div class="body-2 font-weight-medium" :class="eRacunEnabled ? '' : 'grey--text'">eRačun</div>
                    <div class="caption" :class="eracun ? 'primary--text' : 'grey--text'">
                      <template v-if="!eRacunEnabled">
                        Dostupno kada je
                        <span :class="eRacunConditions.providerActive ? 'green--text font-weight-medium' : 'red--text font-weight-medium'">provider aktivan</span>,
                        <span :class="eRacunConditions.fiscalizationDisabled ? 'green--text font-weight-medium' : 'red--text font-weight-medium'">fiskalizacija onemogućena</span>
                        i
                        <span :class="eRacunConditions.customerFromCroatia ? 'green--text font-weight-medium' : 'red--text font-weight-medium'">kupac iz Hrvatske</span>
                      </template>
                      <template v-else-if="eracun">
                        Elektronički račun bit će poslan kupcu
                      </template>
                      <template v-else>
                        Pošalji elektronički račun kupcu
                      </template>
                    </div>
                  </div>
                  <v-switch
                    :value="eracun"
                    @input="handleEracunChange"
                    @change="handleEracunChange"
                    :disabled="!eRacunEnabled"
                    :color="eracun ? 'success' : 'primary'"
                    inset
                    hide-details
                    class="mt-0 pt-0 ml-2"
                  ></v-switch>
                </div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <!-- Email field for eRacun -->
        <v-row
          v-if="paymentTypeValue === 'T' && eracun"
          dense
          class="mt-2 pb-2"
        >
          <v-col cols="12">
            <v-text-field
              :value="eracunEmail"
              @input="$emit('update:eracunEmail', $event)"
              label="Email adresa kontakta Kupca"
              type="email"
              outlined
              dense
              prepend-icon="mdi-email"
              :rules="eracun ? [requiredRule, emailRule] : []"
              hide-details="auto"
            ></v-text-field>
            <div v-if="!canSubmitReceipt" class="error--text caption mt-2">
              <v-icon small color="error">mdi-alert-circle</v-icon>
              <span v-if="!eRacunConditions.allItemsHaveKpd">
                Svi artikli moraju imati KPD šifru.
              </span>
              <span v-if="!eRacunConditions.allZeroVatItemsHaveTaxInfo">
                Artikli s PDV 0% moraju imati odabranu kategoriju poreza i razlog oslobođenja.
              </span>
            </div>
          </v-col>
        </v-row>
        <!-- Attachments for eRacun -->
        <v-row v-if="paymentTypeValue === 'T' && eracun" dense class="pb-2">
          <v-col cols="12">
            <div class="caption grey--text mb-1">Prilozi eRačunu (neobavezno)</div>
            <eracun-attachment-uploader
              :value="eracunAttachments"
              :company-id="companyId"
              @input="$emit('update:eracunAttachments', $event)"
            />
          </v-col>
        </v-row>
      </v-expansion-panel-content>
    </v-expansion-panel>
  </v-expansion-panels>
</template>

<script>
import EracunAttachmentUploader from './EracunAttachmentUploader.vue'

export default {
  name: 'AssociateDetailsPanel',
  components: {
    EracunAttachmentUploader
  },
  data () {
    return {
      oibSearchInput: '',
      nameSearchInput: '',
      internalOibValue: '',
      internalNameValue: '',
      processingSelection: false
    }
  },
  computed: {
    oibModel: {
      get () {
        return this.internalOibValue
      },
      set (value) {
        this.handleOibInput(value)
      }
    },
    nameModel: {
      get () {
        return this.internalNameValue
      },
      set (value) {
        this.handleNameInput(value)
      }
    },
    viesModel: {
      get () {
        return this.selectedVies
      },
      set (value) {
        this.$emit('update:selectedVies', value)
      }
    }
  },
  watch: {
    associateByOib: {
      handler (newVal, oldVal) {
        // When parent updates associateByOib (to string), sync internal value
        if (typeof newVal === 'string') {
          // Only update if the internal value is different to prevent loops
          if (this.internalOibValue !== newVal) {
            this.internalOibValue = newVal

            // Force combobox to update its display value
            if (this.$refs.oibCombobox) {
              this.$nextTick(() => {
                this.$refs.oibCombobox.lazyValue = newVal
              })
            }
          }

          // If we were processing a selection, reset the flag now
          if (this.processingSelection) {
            this.$nextTick(() => {
              this.processingSelection = false
            })
          }
        }
      },
      immediate: true
    },
    associateByName: {
      handler (newVal, oldVal) {
        // When parent updates associateByName (to string), sync internal value
        if (typeof newVal === 'string') {
          // Only update if the internal value is different to prevent loops
          if (this.internalNameValue !== newVal) {
            this.internalNameValue = newVal

            // Force combobox to update its display value
            if (this.$refs.nameCombobox) {
              this.$nextTick(() => {
                this.$refs.nameCombobox.lazyValue = newVal
              })
            }
          }

          // If we were processing a selection, reset the flag now
          if (this.processingSelection) {
            this.$nextTick(() => {
              this.processingSelection = false
            })
          }
        }
      },
      immediate: true
    }
  },
  methods: {
    handleEracunChange (val) {
      this.$emit('update:eracun', val)
    },

    handleCheckboxChange (val) {
      this.$emit('update:doSaveAccount', val)
    },

    handleOibInput (value) {
      // Skip if we're already processing
      if (this.processingSelection) {
        return
      }

      // User cleared the field (clicked X) - reset all fields
      if (value === null || value === undefined || value === '') {
        this.internalOibValue = ''
        this.internalNameValue = ''

        // Force clear both comboboxes
        this.$nextTick(() => {
          if (this.$refs.oibCombobox) {
            this.$refs.oibCombobox.lazyValue = ''
          }
          if (this.$refs.nameCombobox) {
            this.$refs.nameCombobox.lazyValue = ''
          }
        })

        this.$emit('update:associateByOib', '')
        this.$emit('clear-associate-fields')
        return
      }

      // User selected from dropdown - value is an object
      if (value && typeof value === 'object' && (value.oib || (value.oibs && value.oibs[0]))) {
        // Set flag to prevent recursive calls
        this.processingSelection = true

        // Emit the full object so parent can populate fields
        this.$emit('update:associateByOib', value)
        // Trigger select event
        this.$emit('select-associate-oib')

        // Don't set internalOibValue here - let the watcher handle it when parent updates
      } else {
        // User is typing - just update internal value and emit
        this.internalOibValue = value || ''
        this.$emit('update:associateByOib', value)
      }
    },
    handleNameInput (value) {
      // Skip if we're already processing
      if (this.processingSelection) {
        return
      }

      // User cleared the field (clicked X) - reset all fields
      if (value === null || value === undefined || value === '') {
        this.internalNameValue = ''
        this.internalOibValue = ''

        // Force clear both comboboxes
        this.$nextTick(() => {
          if (this.$refs.nameCombobox) {
            this.$refs.nameCombobox.lazyValue = ''
          }
          if (this.$refs.oibCombobox) {
            this.$refs.oibCombobox.lazyValue = ''
          }
        })

        this.$emit('update:associateByName', '')
        this.$emit('clear-associate-fields')
        return
      }

      // User selected from dropdown - value is an object
      if (value && typeof value === 'object' && value.name) {
        // Set flag to prevent recursive calls
        this.processingSelection = true

        // Emit the full object so parent can populate fields
        this.$emit('update:associateByName', value)
        // Trigger select event
        this.$emit('select-associate')

        // Don't set internalNameValue here - let the watcher handle it when parent updates
      } else {
        // User is typing - just update internal value and emit
        this.internalNameValue = value || ''
        this.$emit('update:associateByName', value)
      }
    },
    handleNameChange () {
      // User selected from dropdown - internalNameValue is now an object
      if (this.internalNameValue && typeof this.internalNameValue === 'object') {
        // Emit the full object so parent can populate fields
        this.$emit('update:associateByName', this.internalNameValue)
        // Trigger select event
        this.$emit('select-associate')
        // Convert to string for display
        this.$nextTick(() => {
          this.internalNameValue = this.internalNameValue.name || ''
        })
      }
    }
  },
  props: {
    associatesPanel: {
      type: Array,
      default: () => []
    },
    associates: {
      type: Array,
      required: true
    },
    associateByName: {
      type: [String, Object],
      default: null
    },
    associateAddress: {
      type: String,
      default: ''
    },
    associatesWithOib: {
      type: Array,
      required: true
    },
    associateByOib: {
      type: [String, Object],
      default: null
    },
    associateZip: {
      type: String,
      default: ''
    },
    associateCity: {
      type: String,
      default: ''
    },
    countries: {
      type: Array,
      required: true
    },
    associateCountry: {
      type: String,
      default: ''
    },
    countryLabel: {
      type: String,
      required: true
    },
    cashRegister: {
      type: Object,
      default: null
    },
    costCenters: {
      type: Array,
      default: () => []
    },
    selectedCostCenter: {
      type: Object,
      default: null
    },
    saveAssociateEnabled: {
      type: Boolean,
      default: false
    },
    saveAssociateLabel: {
      type: String,
      required: true
    },
    doSaveAccount: {
      type: Boolean,
      default: false
    },
    inEu: {
      type: Boolean,
      default: false
    },
    isTransactionalReceipt: {
      type: Boolean,
      required: true
    },
    vieses: {
      type: Array,
      default: () => []
    },
    selectedVies: {
      type: [String, Number],
      default: null
    },
    customerTypes: {
      type: Array,
      default: () => []
    },
    selectedCustomerType: {
      type: [String, Number],
      default: null
    },
    paymentTypeValue: {
      type: String,
      required: true
    },
    eracun: {
      type: Boolean,
      default: false
    },
    eRacunEnabled: {
      type: Boolean,
      default: false
    },
    eRacunConditions: {
      type: Object,
      required: true
    },
    eracunEmail: {
      type: String,
      default: ''
    },
    canSubmitReceipt: {
      type: Boolean,
      default: true
    },
    requiredRule: {
      type: Function,
      required: true
    },
    emailRule: {
      type: Function,
      required: true
    },
    loadingOib: {
      type: Boolean,
      default: false
    },
    loadingName: {
      type: Boolean,
      default: false
    },
    eracunAttachments: {
      type: Array,
      default: () => []
    },
    companyId: {
      type: String,
      default: ''
    }
  }
}
</script>

<style scoped>
.transition-card {
  transition: background-color 0.2s ease, border-color 0.2s ease;
}
.blue-border {
  border-color: #1976d2 !important;
}
.disabled-card {
  opacity: 0.75;
}
</style>
